import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import {History} from 'history';
import {scrollToDocumentTop} from './general';
import _ from 'lodash';
import {UserFilters} from '../typing/local-storage';
import {
  BreadcrumbServiceV1,
  BreadcrumbV1,
} from '@volkswagen-onehub/breadcrumb-service';
import {UsedVehicle} from '../typing/used-inventory';
import {userFilters} from '../store/local-storage';
import {NavigationServiceV1} from '@volkswagen-onehub/navigation-service';

const queryParseFormat: any = {
  arrayFormat: 'separator',
  arrayFormatSeparator: ',',
};
/**
 * Get the correct format for the searh from router history
 * @param history Router history
 */
export const getHistorySearch = (history: History) => {
  let search = history.location.search;

  const query = queryString.parse(decodeURI(search));
  delete query['---'];
  search = isEmpty(query) ? '' : `?${queryString.stringify(query)}`;

  return search.replace(/-app/gi, '');
};

/**
 * Add a query parameter to the url
 * @param history React history
 * @param key Key for the query parameter in the url
 * @param value Value for the query parameter in the url
 */
export const addUrlParam = (history: History, key: string, value: string) => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));
  const param = {...query, [key]: value};
  const path = `${history.location.pathname}?${queryString.stringify(param)}`;
  history.replace(path);
};

/**
 * Add a query parameter to the url
 * @param history React history
 * @param userFilters User filters to update in the URL
 * @param skipUpdate Optional flag to skip updating the URL
 * @param keysToSkipOmit Optional array of keys to skip omitting
 */
export const updateUrlFilters = (
  history: History,
  userFilters: UserFilters,
  skipUpdate?: boolean,
  keysToSkipOmit: string[] = [],
) => {
  const search = getHistorySearch(history);
  // Define default keys to omit and filter out those that should be skipped
  const keysToOmit: string[] = [
    'page',
    'distance',
    'sortBy',
    'dealers',
    'modelYear',
    'price',
    'odometer',
    'modelName',
    'fuelType',
    'exteriorColor',
    'interiorColor',
    'transmissionType',
  ].filter(value => !keysToSkipOmit.includes(value));

  let query = queryString.parse(decodeURI(search));
  query = _.omit(query, keysToOmit);
  let stringFilters = '';
  query = userFilters.distance
    ? {...query, distance: userFilters.distance.toString()}
    : query;
  query = userFilters.page
    ? {...query, page: userFilters.page.toString()}
    : query;
  query = userFilters.sortBy
    ? {...query, sortBy: userFilters.sortBy}
    : _.omit(query, 'sortBy');
  if (userFilters?.filters && !_.isEmpty(userFilters?.filters)) {
    const cleanedFilters = _.omitBy(userFilters.filters, _.isEmpty);
    stringFilters = `&${queryString.stringify(
      cleanedFilters,
      queryParseFormat,
    )}`;
  }
  const queryParams = `${queryString.stringify(query)}${stringFilters}`;
  const path = `${history.location.pathname}?${queryParams}`;

  return skipUpdate ? queryParams : history.push(path);
};

/**
 * delete a query parameter to the url
 * @param history React history
 * @param key Key for the query parameter in the url
 * @param value Value for the query parameter in the url
 */
export const deleteUrlParam = (history: History, key: string) => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));
  const param = _.omit(query, [key]);
  const path = `${history.location.pathname}?${queryString.stringify(param)}`;
  history.replace(path);
};

/**
 * Add a query parameter to the url
 * @param history React history
 * @param key Key for the query parameter in the url
 * @param value Value for the query parameter in the url
 */
export const addUrlParamWithPush = (
  history: History,
  key: string,
  value: string | number,
) => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));
  const param = {...query, [key]: value};
  const path = `${history.location.pathname}?${queryString.stringify(param)}`;
  history.push(path);
};

/**
 * Get a zip using the location search from react
 * @param history React history
 */
export const getUrlZip = (history: History): string => {
  const search = getHistorySearch(history);
  const query: any = queryString.parse(decodeURI(search));
  if (!isEmpty(query) && query.zip) {
    const validZipRegex = /^[0-9]{5}$/;
    if (validZipRegex.test(query.zip)) {
      return query.zip;
    }
  }

  return '';
};

/**
 * Get a zip using the location search from react
 * @param history React history
 */
export const getUrlYear = (history: History): number | undefined => {
  const search = getHistorySearch(history);
  const query: any = queryString.parse(decodeURI(search));
  if (!isEmpty(query) && query.year) {
    try {
      return parseInt(query.year);
    } catch (error) {
      console.log('Error parsing model year', error);
      return undefined;
    }
  }
  return undefined;
};

export const setBackToResultsUrl = (history: History, queryParams?: string) => {
  const sanitizedQueryParams = queryParams
    ? queryParams.includes('?')
      ? queryParams
      : `?${queryParams}`
    : '';
  history.push(`/${sanitizedQueryParams}`);
  scrollToDocumentTop();
};

export const setBreadcrumbs = (
  location: any,
  history: History,
  vehicle: UsedVehicle | undefined,
  breadcrumbService?: BreadcrumbServiceV1,
  navigationService?: NavigationServiceV1,
  landingPageName?: string,
) => {
  const breadcrumbs: BreadcrumbV1[] = [];
  if (!breadcrumbService) return;
  // Adding landing access
  const navigateLanding =
    landingPageName &&
    navigationService &&
    navigationService.navigateTo(landingPageName, {});
  let label = 'Certified Pre-Owned';
  let href = '/en/cpo.html';

  if (navigateLanding) href = navigateLanding.createHref();
  const onClick = () => {
    if (navigateLanding) {
      navigateLanding.push();
    } else {
      window.location.href = href;
    }
  };
  breadcrumbs.push({href, label, onClick});

  if (location.pathname === '/') {
    //Results Page
    let label = 'CPO Results';
    let href = location.pathname;

    const onClick = () => {
      // Keep user selected filters
      const userSelectedFilters = userFilters();
      const queryParams = updateUrlFilters(history, userSelectedFilters, true);
      history.push(`${href}?${queryParams}`);
    };
    breadcrumbs.push({href, label, onClick});
  } else {
    //Details page
    let label = 'CPO Results';

    if (vehicle) {
      let href = '/';

      let onClick = () => {
        // Keep user selected filters
        const userSelectedFilters = userFilters();
        const queryParams = updateUrlFilters(
          history,
          userSelectedFilters,
          true,
        );
        history.push(`${href}?${queryParams}`);
      };
      breadcrumbs.push({href, label, onClick});
    }

    let href = `/cpo/vehicle/${vehicle?.vin}`;
    let onClick = () => {
      history.push(href);
    };
    label = `${vehicle?.modelYear} ${vehicle?.model}` || '';

    breadcrumbs.push({href, label, onClick});
  }

  breadcrumbService.set(breadcrumbs);
};

export const filtersToUrl = (
  filters: UserFilters,
  history: History,
  modelYear?: string,
) => {
  filters.distance &&
    addUrlParamWithPush(history, 'distance', filters.distance);
  filters.sortBy && addUrlParamWithPush;
  addUrlParamWithPush(history, 'sortBy', filters?.sortBy || '');
  filters.distance &&
    modelYear &&
    addUrlParamWithPush(history, 'year', modelYear);
};

export const urlToFilters = (
  history: History,
  defaultObject: UserFilters,
): UserFilters => {
  const search = getHistorySearch(history);
  const query: any = queryString.parse(decodeURI(search), queryParseFormat);
  let appliedFilters: UserFilters = defaultObject;
  if (!isEmpty(query)) {
    if (query.distance) {
      appliedFilters.distance = parseInt(query.distance);
    }
    if (query.dealers) {
      const newDealers = _.isArray(query.dealers)
        ? query.dealers
        : [query.dealers];
      appliedFilters.filters = {...appliedFilters.filters, dealers: newDealers};
    }
    if (query.price) {
      appliedFilters.filters = {
        ...appliedFilters.filters,
        price: stringToNumber(query.price),
      };
    }
    if (query.modelName) {
      const newModels = _.isArray(query.modelName)
        ? query.modelName
        : [query.modelName];
      appliedFilters.filters = {
        ...appliedFilters.filters,
        modelName: newModels,
      };
    }
    if (query.modelYear) {
      const newYears = _.isArray(query.modelYear)
        ? query.modelYear
        : [query.modelYear];
      appliedFilters.filters = {...appliedFilters.filters, modelYear: newYears};
    }
    if (query.odometer) {
      appliedFilters.filters = {
        ...appliedFilters.filters,
        odometer: stringToNumber(query.odometer),
      };
    }
    if (query.fuelType) {
      const newFuelType = _.isArray(query.fuelType)
        ? query.fuelType
        : [query.fuelType];
      appliedFilters.filters = {
        ...appliedFilters.filters,
        fuelType: newFuelType,
      };
    }
    if (query.transmissionType) {
      const newTransmissionType = _.isArray(query.transmissionType)
        ? query.transmissionType
        : [query.transmissionType];
      appliedFilters.filters = {
        ...appliedFilters.filters,
        transmissionType: newTransmissionType,
      };
    }
    if (query.exteriorColor) {
      const color = _.isArray(query.exteriorColor)
        ? query.exteriorColor
        : [query.exteriorColor];
      appliedFilters.filters = {
        ...appliedFilters.filters,
        exteriorColor: color,
      };
    }
    if (query.interiorColor) {
      const color = _.isArray(query.interiorColor)
        ? query.interiorColor
        : [query.interiorColor];
      appliedFilters.filters = {
        ...appliedFilters.filters,
        interiorColor: color,
      };
    }
    if (query.page) {
      appliedFilters.page = parseInt(query.page);
    } else {
      appliedFilters.page = 0;
    }
  }

  return appliedFilters;
};

export const stringToNumber = (array: string[]) => {
  const newNumberArray: number[] = [];
  array.map(item => {
    newNumberArray.push(parseInt(item));
  });
  return newNumberArray;
};
export const getFiltersQueryParams = (
  filters: UserFilters,
  history: History,
  modelYear?: string,
) => {
  const search = getHistorySearch(history);
  let query: queryString.ParsedQuery<string> = queryString.parse(
    decodeURI(search),
  );
  // if (!filters.filterList || filters?.filterList.length <= 0) {
  //   query = _.omit(query, 'filters');
  // } else {
  //   query = { ...query, filters: JSON.stringify(filters.filterList) };
  // }
  if (filters.page === 0) {
    query = _.omit(query, 'page');
  } else {
    query = {...query, page: JSON.stringify(filters.page)};
  }
  if (filters.distance)
    query = {...query, distance: filters.distance.toString()};
  if (modelYear) {
    query = {...query, year: modelYear};
  } else {
    query = _.omit(query, ['year']);
  }
  return queryString.stringify(query);
};
